import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { cn } from "@/lib/utils";

/**
 * HeroFundingCard — premium glass metric card shown in the home hero between
 * the subheadline and the CTA row.
 *
 * Two data points side-by-side:
 *   • BUSINESS FUNDING → $20K – $1M   (emerald)
 *   • FUNDING SPEED    → As Fast as 24 Hours*   (white, superscript disclosure)
 *
 * Not interactive. On desktop, columns are split by a thin vertical divider;
 * on mobile the layout stacks with a horizontal divider.
 *
 * The `$20K – $1M` figure animates a single soft emerald glow pulse on
 * mount to guide the eye. Respects `prefers-reduced-motion`.
 */
export const HeroFundingCard = ({ className }) => {
  const reduce = useReducedMotion();

  const pulseAnim = reduce
    ? {}
    : {
        filter: [
          "drop-shadow(0 0 0px rgba(0,230,118,0.0))",
          "drop-shadow(0 0 18px rgba(0,230,118,0.55))",
          "drop-shadow(0 0 0px rgba(0,230,118,0.0))",
        ],
      };

  return (
    <div
      className={cn(
        "group relative overflow-hidden rounded-2xl border border-white/[0.08]",
        "bg-white/[0.03] backdrop-blur-sm",
        "transition-colors duration-300 hover:border-brand-emeraldGlow",
        className,
      )}
      data-testid="hero-funding-card"
    >
      {/* subtle emerald inner glow */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(120% 100% at 20% 0%, rgba(0,230,118,0.10) 0%, rgba(0,0,0,0) 55%)",
        }}
      />

      <div className="relative grid grid-cols-1 divide-y divide-white/[0.08] sm:grid-cols-2 sm:divide-y-0 sm:divide-x">
        {/* LEFT — Business funding range */}
        <div className="px-6 py-5 sm:px-8 sm:py-6">
          <span
            className="block font-mono text-[10px] uppercase tracking-[0.22em] text-text-muted"
            data-testid="hero-funding-eyebrow-amount"
          >
            Business Funding
          </span>
          <motion.p
            className="mt-2 font-display text-[26px] font-bold leading-none tracking-tight text-brand-emerald sm:text-[32px] lg:text-[40px]"
            initial={reduce ? undefined : { opacity: 0, y: 6 }}
            animate={{
              opacity: 1,
              y: 0,
              ...pulseAnim,
            }}
            transition={{
              opacity: { duration: 0.4, delay: 0.15 },
              y: { duration: 0.4, delay: 0.15 },
              filter: {
                duration: 1.5,
                delay: 0.55,
                ease: [0.22, 1, 0.36, 1],
              },
            }}
            data-testid="hero-funding-amount"
          >
            $20K – $1M
          </motion.p>
        </div>

        {/* RIGHT — Funding speed */}
        <div className="px-6 py-5 sm:px-8 sm:py-6">
          <span
            className="block font-mono text-[10px] uppercase tracking-[0.22em] text-text-muted"
            data-testid="hero-funding-eyebrow-speed"
          >
            Funding Speed
          </span>
          <motion.p
            className="mt-2 font-display text-[26px] font-bold leading-none tracking-tight text-white sm:text-[32px] lg:text-[40px]"
            initial={reduce ? undefined : { opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.22 }}
            data-testid="hero-funding-speed"
          >
            As Fast as 24 Hours
            <sup className="ml-0.5 text-brand-emerald text-[0.5em] font-semibold align-top">
              *
            </sup>
          </motion.p>
        </div>
      </div>
    </div>
  );
};

export default HeroFundingCard;
